// Local UI state for the campaigns feature.
// Server state (campaigns, stats, audience previews) lives in RTK Query —
// this slice tracks only cross-component UI values that don't fit neatly in
// a single component (wizard dirty flag for the unsaved-changes prompt,
// persistent list filters, last-viewed detail tab).

import { createSlice } from '@reduxjs/toolkit';
import { WIZARD_STEP } from './constants';

const initialState = {
  list: {
    filters: {
      search: '',
      status: null,
      channel: null,
      offer: null,
      dateRange: { preset: 'thisMonth', from: null, to: null },
    },
    page: 1,
  },
  wizard: {
    activeStep: WIZARD_STEP.BASICS,
    maxReachedStep: WIZARD_STEP.BASICS,
    dirty: false,
    lastSavedAt: null,
  },
  detail: {
    activeTab: 'overview',
  },
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setListFilters(state, { payload }) {
      state.list.filters = { ...state.list.filters, ...payload };
      state.list.page = 1;
    },
    clearListFilters(state) {
      state.list.filters = initialState.list.filters;
      state.list.page = 1;
    },
    setListPage(state, { payload }) {
      state.list.page = payload;
    },

    setWizardStep(state, { payload }) {
      state.wizard.activeStep = payload;
      if (payload > state.wizard.maxReachedStep) state.wizard.maxReachedStep = payload;
    },
    setWizardMaxReached(state, { payload }) {
      state.wizard.maxReachedStep = Math.max(state.wizard.maxReachedStep, payload);
    },
    setWizardDirty(state, { payload }) {
      state.wizard.dirty = payload;
    },
    markWizardSaved(state) {
      state.wizard.lastSavedAt = new Date().toISOString();
      state.wizard.dirty = false;
    },
    resetWizard(state) {
      state.wizard = initialState.wizard;
    },

    setDetailTab(state, { payload }) {
      state.detail.activeTab = payload;
    },
  },
});

export const {
  setListFilters,
  clearListFilters,
  setListPage,
  setWizardStep,
  setWizardMaxReached,
  setWizardDirty,
  markWizardSaved,
  resetWizard,
  setDetailTab,
} = campaignsSlice.actions;

// --- Selectors ---------------------------------------------------------------

export const selectListFilters = (state) => state.campaigns.list.filters;
export const selectListPage = (state) => state.campaigns.list.page;
export const selectWizardStep = (state) => state.campaigns.wizard.activeStep;
export const selectWizardMaxReached = (state) => state.campaigns.wizard.maxReachedStep;
export const selectWizardDirty = (state) => state.campaigns.wizard.dirty;
export const selectWizardLastSavedAt = (state) => state.campaigns.wizard.lastSavedAt;
export const selectDetailTab = (state) => state.campaigns.detail.activeTab;

export default campaignsSlice.reducer;
